import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Loader2, Play, RotateCcw } from 'lucide-react'
import { AudioWaveformVisualization } from '../shared/AudioWaveformVisualization'

const MIN_TRIM_SECONDS = 0.05

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.max(min, Math.min(max, value))
}

function formatSeconds(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '00:00.0'
  const minutes = Math.floor(value / 60)
  const seconds = value - minutes * 60
  return `${String(minutes).padStart(2, '0')}:${seconds.toFixed(1).padStart(4, '0')}`
}

function inputNumber(value: string): number | null {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export interface SoundExpertTrimTabProps {
  audioUrl: string | null
  sourcePath: string | undefined
  durationSeconds: number | undefined
  disabled: boolean
  showWaveform?: boolean
  onSaveTrim: (range: { startS: number; endS: number }) => Promise<void>
}

const SoundExpertTrimTab = memo(function SoundExpertTrimTab({
  audioUrl,
  sourcePath,
  durationSeconds,
  disabled,
  showWaveform = true,
  onSaveTrim,
}: SoundExpertTrimTabProps) {
  const { t } = useTranslation('canvas')
  const previewRef = useRef<HTMLAudioElement>(null)
  const [loadedDuration, setLoadedDuration] = useState(0)
  const [startS, setStartS] = useState(0)
  const [endS, setEndS] = useState(0)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const duration = useMemo(() => {
    const candidate = durationSeconds && durationSeconds > 0 ? durationSeconds : loadedDuration
    return Number.isFinite(candidate) && candidate > 0 ? candidate : 0
  }, [durationSeconds, loadedDuration])

  useEffect(() => {
    setStartS(0)
    setEndS(duration)
    setError(null)
    setIsPreviewing(false)
  }, [duration, sourcePath])

  const selectedDuration = Math.max(0, endS - startS)
  const hasValidAudio = !!audioUrl && !!sourcePath && duration > 0
  const isNoOp = startS <= 0.01 && endS >= duration - 0.01
  const canSave = hasValidAudio && !disabled && !isSaving && selectedDuration >= MIN_TRIM_SECONDS && !isNoOp
  const startPercent = duration > 0 ? (startS / duration) * 100 : 0
  const endPercent = duration > 0 ? (endS / duration) * 100 : 100

  const updateStart = useCallback((value: number) => {
    setError(null)
    setStartS(clamp(value, 0, Math.max(0, endS - MIN_TRIM_SECONDS)))
  }, [endS])

  const updateEnd = useCallback((value: number) => {
    setError(null)
    setEndS(clamp(value, Math.min(duration, startS + MIN_TRIM_SECONDS), duration))
  }, [duration, startS])

  const handlePreview = useCallback(() => {
    const el = previewRef.current
    if (!el || !hasValidAudio) return
    el.currentTime = startS
    setIsPreviewing(true)
    void el.play()
  }, [hasValidAudio, startS])

  const handleSave = useCallback(async () => {
    if (!canSave) return
    setIsSaving(true)
    setError(null)
    try {
      await onSaveTrim({ startS, endS })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('soundExpertNode.trimFailed'))
    } finally {
      setIsSaving(false)
    }
  }, [canSave, endS, onSaveTrim, startS, t])

  return (
    <div className="h-full overflow-y-auto nowheel px-3 py-3" data-testid="sound-expert-trim-tab">
      {audioUrl ? (
        <>
          {showWaveform ? (
            <AudioWaveformVisualization
              url={audioUrl}
              height={78}
              accentColor="var(--primary)"
              className="mb-3"
              compact
            />
          ) : null}
          <audio
            ref={previewRef}
            src={audioUrl}
            preload="metadata"
            onLoadedMetadata={(event) => {
              const mediaDuration = event.currentTarget.duration
              if (Number.isFinite(mediaDuration) && mediaDuration > 0) {
                setLoadedDuration(mediaDuration)
              }
            }}
            onTimeUpdate={(event) => {
              if (event.currentTarget.currentTime >= endS) {
                event.currentTarget.pause()
                event.currentTarget.currentTime = startS
                setIsPreviewing(false)
              }
            }}
            onPause={() => setIsPreviewing(false)}
            onEnded={() => setIsPreviewing(false)}
          />
        </>
      ) : (
        <div className="flex h-24 items-center justify-center text-xs font-medium text-text-muted">
          {t('soundExpertNode.trimNoAudio')}
        </div>
      )}

      <div className="space-y-3" aria-disabled={!hasValidAudio || disabled}>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xxs text-muted-foreground">
            <span>{t('soundExpertNode.trimWindow')}</span>
            <span className="font-mono tabular-nums text-foreground">
              {formatSeconds(selectedDuration)}
            </span>
          </div>
          <div className="relative h-3 rounded-full bg-surface-2">
            <div
              className="absolute top-0 h-3 rounded-full bg-primary/25"
              style={{ left: `${startPercent}%`, width: `${Math.max(0, endPercent - startPercent)}%` }}
            />
          </div>
          <div className="space-y-2">
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={startS}
              disabled={!hasValidAudio || disabled}
              onChange={(event) => updateStart(Number(event.currentTarget.value))}
              aria-label={t('soundExpertNode.trimStart')}
              className="w-full accent-primary disabled:opacity-40"
              data-testid="sound-trim-start-slider"
            />
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={endS}
              disabled={!hasValidAudio || disabled}
              onChange={(event) => updateEnd(Number(event.currentTarget.value))}
              aria-label={t('soundExpertNode.trimEnd')}
              className="w-full accent-primary disabled:opacity-40"
              data-testid="sound-trim-end-slider"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <label className="space-y-1">
            <span className="text-xxs text-muted-foreground">{t('soundExpertNode.trimStart')}</span>
            <input
              type="number"
              min={0}
              max={duration}
              step={0.1}
              value={startS.toFixed(1)}
              disabled={!hasValidAudio || disabled}
              onChange={(event) => {
                const parsed = inputNumber(event.currentTarget.value)
                if (parsed !== null) updateStart(parsed)
              }}
              className="h-9 w-full rounded-lg border border-input-border bg-input-bg px-2 text-xs text-foreground tabular-nums outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-40"
              data-testid="sound-trim-start-input"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xxs text-muted-foreground">{t('soundExpertNode.trimEnd')}</span>
            <input
              type="number"
              min={0}
              max={duration}
              step={0.1}
              value={endS.toFixed(1)}
              disabled={!hasValidAudio || disabled}
              onChange={(event) => {
                const parsed = inputNumber(event.currentTarget.value)
                if (parsed !== null) updateEnd(parsed)
              }}
              className="h-9 w-full rounded-lg border border-input-border bg-input-bg px-2 text-xs text-foreground tabular-nums outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-40"
              data-testid="sound-trim-end-input"
            />
          </label>
          <div className="space-y-1">
            <span className="block text-xxs text-muted-foreground">{t('soundExpertNode.trimLength')}</span>
            <div className="flex h-9 items-center rounded-lg border border-border-subtle bg-surface-0 px-2 text-xs font-medium text-foreground tabular-nums">
              {formatSeconds(selectedDuration)}
            </div>
          </div>
        </div>

        {error ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-foreground">
            {error}
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            type="button"
            disabled={!hasValidAudio || disabled}
            onClick={() => {
              setStartS(0)
              setEndS(duration)
              setError(null)
            }}
            className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground transition-colors hover:bg-surface-3 hover:border-input-border-hover disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="sound-trim-reset"
          >
            <span className="flex items-center gap-1.5">
              <RotateCcw className="h-4 w-4" />
              {t('soundExpertNode.trimReset')}
            </span>
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!hasValidAudio || disabled}
              onClick={handlePreview}
              className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground transition-colors hover:bg-surface-3 hover:border-input-border-hover disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="sound-trim-preview"
            >
              <span className="flex items-center gap-1.5">
                <Play className="h-4 w-4" />
                {isPreviewing ? t('soundExpertNode.trimPreviewing') : t('soundExpertNode.trimPreview')}
              </span>
            </button>
            <button
              type="button"
              disabled={!canSave}
              onClick={handleSave}
              className="h-9 rounded-lg bg-primary px-3 text-xs font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="sound-trim-save"
            >
              <span className="flex items-center gap-1.5">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {isSaving ? t('soundExpertNode.trimSaving') : t('soundExpertNode.trimSave')}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
})

SoundExpertTrimTab.displayName = 'SoundExpertTrimTab'

export { SoundExpertTrimTab, formatSeconds as formatTrimSeconds }
